import { DataTable } from "@/components/data-table/DataTable";
import { DataTableColumnHeader } from "@/components/data-table/DataTableColumnHeader";
import { getReadableTextColorFromBackgroundColorWithOpacity } from "@/lib/color-utils";
import { buildDataTableQueryOptions, ensureRouteQueryData } from "@/lib/route-loader";
import { searchStringSearchSchema } from "@/lib/route-search";
import type { OrganizationRoleListItemAdminDTO } from "@/orval/model";
import {
  adminOrganizationRoleGetOrganizationRoles,
  getAdminOrganizationRoleGetOrganizationRolesQueryKey,
  getAdminOrganizationRoleGetOrganizationRolesQueryOptions,
} from "@/orval/admin-organization-role";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { PortalPageHeader } from "@/components/portal-page-header";

const colorColumn: ColumnDef<OrganizationRoleListItemAdminDTO, string> = {
  accessorKey: "color",
  header: "Color",
  cell: (context) => {
    const value = context.getValue();
    return (
      <div className="flex flex-row gap-1">
        <span
          className="px-3 py-1 rounded-full text-xs font-semibold text-foreground"
          style={{ background: value, color: getReadableTextColorFromBackgroundColorWithOpacity(value, 0.75) }}
        >
          {value}
        </span>
      </div>
    );
  },
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const columns: ColumnDef<OrganizationRoleListItemAdminDTO, any>[] = [
  {
    header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
    accessorKey: "roleName",
  },
  colorColumn,
];

const OrganizationAdminRolesPage = () => {
  const { organizationId } = Route.useParams();

  const getRoles = adminOrganizationRoleGetOrganizationRoles;

  return (
    <div className="flex flex-1 min-h-0 flex-col p-4 md:p-6">
      <PortalPageHeader className="mb-8" title="Roles" />

      <DataTable
        columns={columns}
        queryFn={(params, signal) => getRoles(organizationId, params, signal)}
        queryKeyFn={(params) => getAdminOrganizationRoleGetOrganizationRolesQueryKey(organizationId, params)}
        rowLink={(row) => ({ from: Route.fullPath, to: row.id.toString() })}
        getRowIdFn={(row) => row.id.toString()}
      />
    </div>
  );
};

export const Route = createFileRoute("/_app/organizations/$organizationId/admin/roles")({
  validateSearch: searchStringSearchSchema,
  loader: ({ params, location, ...loaderContext }) => {
    const { searchString } = searchStringSearchSchema.parse(location.search);
    return ensureRouteQueryData(
      loaderContext,
      getAdminOrganizationRoleGetOrganizationRolesQueryOptions(
        params.organizationId,
        buildDataTableQueryOptions({ searchString }),
      ),
    );
  },
  component: OrganizationAdminRolesPage,
});
